import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Youtube } from "lucide-react";

const videos = [
    {
        id: "zclYZLoqO10",
        title: "Powerful Pressure Washing in Action",
        tag: "Our Work",
        quality: "hqdefault",
    },
    {
        id: "jDIqBSgILr8",
        title: "Restoring Surfaces to Like-New",
        tag: "Before & After",
        quality: "maxresdefault",
    },
    {
        id: "yCNIopu-Pyg",
        title: "Stronger Solutions — Company Showcase",
        tag: "Meet The Team",
        quality: "maxresdefault",
    },
];

function VideoCard({ video, index, featured }) {
    const [playing, setPlaying] = useState(false);

    const thumb = `https://i.ytimg.com/vi/${video.id}/${
        video.quality || "hqdefault"
    }.jpg`;
    const thumbFallback = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            className={`group relative overflow-hidden rounded-sm border border-black/10 bg-black shadow-lg ${
                featured ? "lg:col-span-2" : ""
            }`}
            data-testid={`video-card-${index}`}
        >
            <div className="relative w-full aspect-video">
                {playing ? (
                    <iframe
                        src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
                        title={video.title}
                        className="absolute inset-0 w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        loading="lazy"
                        data-testid={`video-iframe-${index}`}
                    />
                ) : (
                    <button
                        onClick={() => setPlaying(true)}
                        aria-label={`Play ${video.title}`}
                        data-testid={`video-play-${index}`}
                        className="absolute inset-0 w-full h-full cursor-pointer"
                    >
                        <img
                            src={thumb}
                            onError={(e) => {
                                e.currentTarget.src = thumbFallback;
                            }}
                            alt={video.title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {/* dark gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/20" />

                        {/* play button */}
                        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                            <span className="absolute w-16 h-16 rounded-full bg-[#1E88E5] opacity-40 group-hover:animate-ping" />
                            <span className="relative w-16 h-16 rounded-full bg-[#1E88E5] flex items-center justify-center shadow-[0_8px_30px_rgba(30,136,229,0.5)] transition-transform duration-300 group-hover:scale-110">
                                <Play className="w-6 h-6 text-white fill-white ml-1" />
                            </span>
                        </span>

                        {/* meta */}
                        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-[10px] font-bold uppercase tracking-widest text-[#0a0a0a] px-3 py-1.5 rounded-sm">
                            <Youtube className="w-3.5 h-3.5 text-red-600" />
                            {video.tag}
                        </span>
                        <span
                            className={`absolute left-4 right-4 bottom-4 text-left font-display font-bold uppercase text-white leading-tight ${
                                featured ? "text-2xl sm:text-3xl" : "text-lg"
                            }`}
                        >
                            {video.title}
                        </span>
                    </button>
                )}
            </div>
        </motion.div>
    );
}

export default function Videos() {
    return (
        <section
            id="videos"
            data-testid="videos-section"
            className="relative py-24 lg:py-32 bg-white border-t border-black/5"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#1E88E5] font-bold mb-4">
                            / Watch Us Work
                        </div>
                        <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#0a0a0a]">
                            See the power
                            <br />
                            <span className="text-[#1E88E5]">
                                in motion.
                            </span>
                        </h2>
                    </div>
                    <p className="max-w-md text-gray-600 text-base">
                        Real jobs, real results. Watch our crew transform
                        driveways, decks and storefronts across Georgia — no
                        edits, no shortcuts.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {videos.map((v, i) => (
                        <VideoCard
                            key={v.id}
                            video={v}
                            index={i}
                            featured={i === 0}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
